import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface DisponibilidadLlave {
  disponible: boolean;
  llave?: string;
  /** Qué mostrarle al usuario cuando no se puede usar. Ya viene redactado. */
  mensaje?: string;
}

export interface ReservaLlave {
  llave: string;
  expiraAt: string;
}

export interface ResultadoIntencion {
  registrado: boolean;
  /** La llave usada. El backend no devuelve el nombre del referidor: era un
   *  oráculo enumerable de clientes en un endpoint público. */
  llaveReferidor?: string;
  motivo?:
    | 'LLAVE_INEXISTENTE'
    | 'AUTO_REFERIDO'
    | 'YA_VINCULADO'
    | 'PROGRAMA_APAGADO'
    | 'SIN_CORREO_EN_TOKEN'
    | 'ERROR_RED'
    | string;
}

export interface ReferidoItem {
  nombre: string;
  registradoAt: string;
  fondeo: boolean;
  bonoEstado: 'RETENIDO' | 'LIBERADO' | 'VENCIDO' | 'ANULADO' | string;
  montoUsdt: string;
  /** La moneda de la campana de ese vinculo. No siempre es USDT. */
  moneda: string;
  /**
   * Que falta para que ese bono se libere, ya redactado por el backend.
   *
   * `null` si ya cobro, o si el job todavia no evaluo el vinculo. En ese caso
   * la pantalla no promete nada: no dice nada.
   */
  falta: string | null;
}

/**
 * Lo que exige y paga la campaña, tal como la configuró el operador. Lo manda
 * el API; el front no escribe montos ni reglas a mano: la campaña puede
 * cambiar cualquier día y el texto tiene que cambiar con ella.
 */
export interface CondicionesCampana {
  moneda: string;
  bonoReferidor: number;
  bonoReferido: number;
  /** Cuándo cobra quien invita: `AL_FONDEAR`, `AL_KYC` o `AL_PRIMER_ENVIO`. */
  gatilloReferidor: string;
  /** 0 = no se le exige actividad a quien invita. */
  actividadReferidorDias: number;
  /** `null`: esa moneda no califica. */
  pisoFondeoClp: number | null;
  pisoFondeoVes: number | null;
  pisoFondeoUsdt: number | null;
  /** Días que tiene el invitado, desde su registro, para cumplir. */
  reservaDias: number;
  alcance: { codigo: string; nombre: string; universal: boolean };
  vigenciaHasta: string | null;
}

export interface ProgramaReferidos {
  activo: boolean;
  bonoReferidorUsdt: number;
  bonoReferidoUsdt: number;
  /** `null` cuando el piso no está configurado: nadie califica por esa moneda. */
  fondeoMinClp: number | null;
  fondeoMinVes: number | null;
  fondeoMinUsdt: number | null;
  /**
   * Las de la campaña para todos (`TODOS`), si es la que está activa.
   * Opcional: un API anterior no la manda, y entonces no se promete nada.
   * Con campañas por país viene en `null`: a un anónimo no se le promete
   * ninguna sin saber de dónde es.
   */
  condiciones?: CondicionesCampana | null;
  /** Las de cada campaña activa, una por país. Ausente con un API anterior. */
  condicionesPorAlcance?: CondicionesCampana[];
}

/** El documento que eligió el invitado en el registro. */
export type DocumentoRegistro = 'chile' | 'venezuela' | 'other' | null | undefined;

/**
 * Si el cliente con sesión ve algo de referidos, y con qué condiciones.
 *
 * `visible` lo decide el backend: programa encendido **y** una campaña activa
 * de su país (Chile o Venezuela, por su documento). Es la única fuente de
 * visibilidad del menú, la pantalla, la tarjeta del home y el aviso de
 * bienvenida.
 */
export interface MiProgramaReferidos {
  visible: boolean;
  condiciones: CondicionesCampana | null;
  cuota: CupoReferidor;
}

const SIN_PROGRAMA: MiProgramaReferidos = Object.freeze({
  visible: false,
  condiciones: null,
  cuota: { disponible: false, restante: 0 },
}) as MiProgramaReferidos;

export interface ConfigReferidos {
  activo: boolean;
  bonoReferidorUsdt: number;
  bonoReferidoUsdt: number;
  fondeoMinClp: number | null;
  fondeoMinVes: number | null;
  fondeoMinUsdt: number | null;
  actividadDias: number;
  topePorUsuario: number;
  topeMensualUsdt: number;
  topeMensualVes: number;
  /** Bolsa a partir de la cual activar exige un segundo operador. 0 = siempre. */
  dobleControlUsdt: number;
  dobleControlVes: number;
}

// ---------------------------------------------------------------------------
// Campanas de referidos
// ---------------------------------------------------------------------------

export type CampanaTipo = 'GLOBAL' | 'ASIGNADA';
export type CampanaEstado =
  | 'BORRADOR'
  | 'PROGRAMADA'
  | 'ACTIVA'
  | 'PAUSADA'
  | 'CERRADA';
export type GatilloReferidor = 'AL_KYC' | 'AL_FONDEAR' | 'AL_PRIMER_ENVIO';

/**
 * Una campana tal como la devuelve el listado del panel.
 *
 * `cupoRestante` y `bolsaLibre` vienen calculados del servidor a proposito: son
 * dos numeros que se muestran en dos pantallas distintas, y si cada una los
 * derivara por su cuenta podrian terminar diciendo cosas diferentes del mismo
 * dato.
 */
export interface CampanaReferidos {
  referidoCampanaId: number;
  nombre: string;
  codigo: string;
  tipo: CampanaTipo;
  estado: CampanaEstado;
  prioridad: number;
  alcanceCodigo: string;
  cuotaPorReferidor: number | null;
  gatilloReferidor: GatilloReferidor;
  actividadReferidorDias: number;
  /** Qué cuenta como «cargar». `null` en una moneda = esa moneda no califica. */
  pisoFondeoClp: string | null;
  pisoFondeoVes: string | null;
  pisoFondeoUsdt: string | null;
  moneda: string;
  montoReferidor: string;
  montoReferido: string;
  unidadesTotal: number;
  unidadesUsadas: number;
  cupoRestante: number;
  presupuesto: string;
  comprometido: string;
  pagado: string;
  bolsaLibre: string;
  vigenciaDesde: string;
  vigenciaHasta: string | null;
  reservaDias: number;
  pausaPct: number | null;
  pausaMinutos: number | null;
  pausadaAt: string | null;
  pausadaMotivo: 'VELOCIDAD' | 'MANUAL' | null;
  acumulaConBonos: boolean;
  creadaPorEmail: string | null;
  activadaPorEmail: string | null;
  activadaAt: string | null;
  referidores: number;
}

export interface OpcionesCampana {
  alcances: Array<{ alcance: string; nombre: string; monedas: string[] }>;
  monedasAcreditables: string[];
  tipos: CampanaTipo[];
  gatillos: GatilloReferidor[];
}

export interface CrearCampanaReferidos {
  nombre: string;
  codigo: string;
  tipo: CampanaTipo;
  prioridad?: number;
  alcanceCodigo: string;
  cuotaPorReferidor?: number | null;
  gatilloReferidor?: GatilloReferidor;
  actividadReferidorDias?: number;
  pisoFondeoClp?: number;
  pisoFondeoVes?: number;
  pisoFondeoUsdt?: number;
  moneda: string;
  montoReferidor: number;
  montoReferido: number;
  unidadesTotal: number;
  vigenciaDesde: string;
  vigenciaHasta?: string;
  reservaDias?: number;
  pausaPct?: number | null;
  pausaMinutos?: number | null;
  acumulaConBonos?: boolean;
}

export interface AsignacionCampana {
  id: number;
  usuarioId: number;
  nombre: string;
  email: string | null;
  llave: string | null;
  cuota: number | null;
  cuotaUsada: number;
  exclusiva: boolean;
  activa: boolean;
  desde: string | null;
  hasta: string | null;
  asignadaPorEmail: string | null;
  asignadaAt: string;
  invitados: number;
  conFondeo: number;
}

/** Lo que se puede prometer todavia, y lo que se paga si los retenidos cumplen. */
export interface RiesgoCampana {
  moneda: string;
  presupuesto: number;
  comprometido: number;
  pagado: number;
  /** Deuda contingente: lo prometido que todavia no salio. */
  enRiesgo: number;
  bolsaLibre: number;
  cupos: { total: number; usados: number; restantes: number };
  vinculos: number;
  ladosRetenidos: number;
  ladosVencidos: number;
}

export interface FugaCampana {
  items: Array<{
    motivo: string;
    lado: 'referidor' | 'referido';
    /** La moneda del monto. Ausente con un API anterior. */
    moneda?: string;
    total: number;
    monto: number;
    /** `true` si ya no va a cambiar solo: el panel lo pinta como fuga. */
    terminal: boolean;
  }>;
  esperando: { lados: number; monto: number };
  fugados: { lados: number; monto: number };
}

export interface RetornoCampana {
  moneda: string;
  pagado: number;
  /** Estimacion declarada con el margen del riel, no una medicion. */
  volumenNecesarioUsd: number;
  volumenPorUnidadUsd: number;
  invitadosQueCargaron: number;
  invitadosQueOperaron: number;
  conversionAOperar: number;
  pegadosAlMinimo: number;
  pegadosAlMinimoPct: number;
  /** Cargaron el minimo y nunca operaron: la perdida directa. */
  minimoYSeFueron: number;
}

export interface TableroCampana {
  riesgo: RiesgoCampana | null;
  fuga: FugaCampana;
  retorno: RetornoCampana;
}

export interface RendimientoCampana {
  campanaId: number;
  codigo: string;
  nombre: string;
  tipo: CampanaTipo;
  estado: CampanaEstado;
  /** País de la campaña: `CL`, `VE` o `TODOS`. Ausente con un API anterior. */
  alcance?: string;
  moneda: string;
  cupos: number;
  cuposUsados: number;
  referidores: number;
  invitados: number;
  conFondeo: number;
  conversion: number;
  presupuesto: number;
  comprometido: number;
  pagado: number;
  /** `null` —y no 0— cuando todavia no hay invitados activos. */
  costoPorActivo: number | null;
}

export interface RendimientoReferidor {
  usuarioId: number;
  nombre: string;
  llave: string | null;
  email: string | null;
  invitados: number;
  conFondeo: number;
  conversion: number;
  bonosCobrados: number;
  cobrado: number;
  moneda: string;
  campanas: number;
}

/** Una fila de la pantalla centralizada de registrados con llave. */
export interface UsuarioConLlave {
  referidoId: number;
  estado: string;
  registradoAt: string;
  invitado: {
    usuarioId: number | null;
    nombre: string;
    email: string | null;
    llave: string | null;
    complianceAprobado: boolean;
  };
  referidor: { usuarioId: number; nombre: string; llave: string };
  campana: { codigo: string; nombre: string; tipo: CampanaTipo } | null;
  fondeo: { fecha: string; moneda: string | null; monto: string | null } | null;
  bonoInvitado: {
    estado: string;
    motivo: string | null;
    monto: string;
    moneda: string;
    liberadoAt: string | null;
  };
  bonoReferidor: {
    estado: string;
    motivo: string | null;
    monto: string;
    moneda: string;
  };
}

/** Un monto en su moneda. No se convierte: sumar 5 USDT con 300 VES da 305 de nada. */
export interface MontoPorMoneda {
  moneda: string;
  monto: number;
}

export interface VolumenPorMoneda {
  moneda: string;
  operaciones: number;
  monto: number;
}

/**
 * Lo que costo una campana y lo que se estima que dejo.
 *
 * `inversion` y `volumen` son exactos. `margenEstimado` NO: sale de aplicarle
 * al volumen el margen del riel, porque no hay columna de ganancia en la base.
 * La pantalla tiene que decirlo — por eso el backend manda los supuestos.
 */
export interface FilaInversionRetorno {
  campanaId: number;
  codigo: string;
  nombre: string;
  tipo: CampanaTipo;
  estado: CampanaEstado;
  inversion: {
    moneda: string;
    pagado: number;
    /** Lo que va a salir si los retenidos cumplen. */
    comprometidoVivo: number;
    total: number;
    presupuesto: number;
  };
  invitados: number;
  activos: number;
  operaciones: number;
  volumen: VolumenPorMoneda[];
  margenEstimado: MontoPorMoneda[];
  /** El pedazo expresable en dolares sin inventar una tasa. Es un piso. */
  margenUsdMedible: number;
  /** `null` cuando compararlos exigiria una tasa que el reporte no tiene. */
  cobertura: number | null;
  coberturaNota: string | null;
}

export interface InversionRetorno extends Pagina<FilaInversionRetorno> {
  totales: {
    inversion: Array<{
      moneda: string;
      campanas: number;
      pagado: number;
      comprometidoVivo: number;
      presupuesto: number;
    }>;
    invitados: number;
    cargaron: number;
    activos: number;
    operaciones: number;
    volumen: VolumenPorMoneda[];
    margenEstimado: MontoPorMoneda[];
    margenUsdMedible: number;
  };
  supuestos: {
    margenPorRiel: Array<{ destino: string; pct: number }>;
    nota: string;
    volumenNota: string;
  };
}

/** Una persona con plata del programa, sumando sus dos papeles. */
export interface UsuarioConBono {
  usuarioId: number;
  nombre: string;
  email: string | null;
  llave: string | null;
  /** Cuantos bonos le corresponden por la gente que trajo. */
  comoReferidor: number;
  /** Y cuantos por haberse registrado con la llave de alguien. */
  comoInvitado: number;
  bonos: { cobrados: number; pendientes: number; perdidos: number };
  montos: Array<{ moneda: string; cobrado: number; pendiente: number }>;
  /** Por que sigue esperando, si espera algo. */
  motivoPendiente: string | null;
}

/** La forma que devuelven todos los listados del panel. */
export interface Pagina<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

export interface ResumenAdmin {
  activo: boolean;
  vinculos: Record<string, number>;
  bonos: { liberados: number; retenidos: number };
  liberadoUsdt: number;
  /** Deuda contingente: bonos prometidos que se pagan si esos invitados cargan. */
  retenidoUsdt: number;
  liberadoEsteMesUsdt: number;
  /**
   * Lo mismo sin mezclar monedas. Los tres campos `...Usdt` suman todas las
   * monedas y se llaman así por historia. Ausente con un API anterior.
   */
  porMoneda?: Array<{ moneda: string; liberado: number; retenido: number; liberadoEsteMes: number }>;
  conversion: { vinculados: number; conFondeo: number; porcentaje: number };
}

export interface PuntaVinculo {
  usuarioId: number | null;
  /** Nombre y apellido, o el correo si el alta todavía no se completó. */
  nombre: string;
  email: string | null;
}

export interface BonoLado {
  estado: string;
  montoUsdt: string;
  /** La moneda del monto (USDT o VES). Ausente con un API anterior. */
  moneda?: string;
  liberadoAt: string | null;
}

export interface BonoAdminItem {
  referidoId: number;
  estado: string;
  origen: string;
  llaveReferidor: string;
  referidor: PuntaVinculo;
  invitado: PuntaVinculo;
  fondeo: { fecha: string; moneda: string | null } | null;
  bonoReferidor: BonoLado;
  bonoReferido: BonoLado;
  createdAt: string;
}

export interface FiltrosBonos {
  page?: number;
  limit?: number;
  /** `ESPERANDO_ALTA` | `VINCULADO` | `ANULADO` */
  estado?: string;
  /** `PENDIENTE` si algún bono sigue retenido, `COBRADO` si los dos salieron. */
  bono?: string;
  q?: string;
}

export interface TopReferidor {
  usuarioId: number;
  nombre: string | null;
  llave: string | null;
  invitados: number;
  conFondeo: number;
  bonosCobrados: number;
  /** Lo cobrado, en `moneda`. Con `VARIAS` es una suma de monedas distintas. */
  cobradoUsdt: number;
  moneda?: string;
}

export interface FichaReferidos {
  usuarioId: number;
  llave: string | null;
  invitadoPor: {
    usuarioId: number;
    nombre: string | null;
    llave: string;
    origen: string;
    estado: string;
    bonoEstado: string;
    fondeoDetectadoAt: string | null;
    fecha: string;
  } | null;
  trajo: {
    invitados: number;
    conFondeo: number;
    bonosCobrados: number;
    cobradoUsdt: number;
    /** La moneda de lo cobrado; `VARIAS` si fue en más de una. */
    moneda?: string;
  };
}

/**
 * Lo que responde el endpoint público al abrir un link: si la llave existe y
 * las condiciones de la campaña de ese referidor. Nunca datos de la persona.
 */
export interface ReferidorPublico {
  existe: boolean;
  llave?: string;
  condiciones?: CondicionesCampana | null;
}

/** Lo que le queda al cliente para seguir cobrando por invitar. */
export interface CupoReferidor {
  /** `true` si hoy hay una campana que pueda pagarle un par mas. */
  disponible: boolean;
  /** Cuantos pares mas le paga esa campana. `null` es sin tope. */
  restante: number | null;
}

export interface ResumenReferidos {
  liberadoUsdt: number;
  pendienteUsdt: number;
  invitados: number;
  invitadosQueFondearon: number;
  miBono: {
    montoUsdt: number;
    moneda: string;
    estado: string;
    falta: string | null;
  } | null;
  /**
   * Opcional a proposito: es campo nuevo del API y el front puede estar
   * desplegado antes. Quien lo lea para decidir si muestra algo tiene que
   * tratar la ausencia como "no mostrar", que es el lado seguro.
   */
  cuota?: CupoReferidor;
  /**
   * Las de la campaña que hoy les tocaría a sus invitados. `null` si ya no le
   * queda cupo; ausente con un API anterior. En los dos casos no se promete.
   */
  condiciones?: CondicionesCampana | null;
  /**
   * Ganado y esperando, por moneda: un bono en Bs. y otro en USDT no se
   * suman. Ausente con un API anterior; entonces se muestran los totales
   * viejos, que el API siempre suma en USDT.
   */
  totales?: Array<{ moneda: string; liberado: number; pendiente: number }>;
}

/**
 * La llave del usuario y su programa de referidos.
 *
 * Junta las llamadas públicas (las del registro, cuando todavía no hay sesión)
 * y las autenticadas (las del módulo). Van en un solo servicio porque son la
 * misma función vista desde dos momentos, y separarlas obligaría a duplicar
 * los tipos.
 *
 * Las públicas mandan el header `skip`, que es como el resto del registro
 * evita el interceptor de sesión.
 */
@Injectable({ providedIn: 'root' })
export class LlaveService {
  private baseUrl = environment.apiNest;

  constructor(private http: HttpClient) {}

  private publicHeaders(): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/json', skip: 'true' });
  }

  // ---------------------------------------------------------------------
  // Registro (público)
  // ---------------------------------------------------------------------

  /**
   * ¿Se puede usar esta llave? Se llama mientras el usuario tipea.
   *
   * Nunca lanza, y ante un fallo responde que SÍ se puede usar. Es deliberado:
   * esta consulta es una comodidad para avisar temprano, no la autoridad. La
   * autoridad es el backend, que vuelve a validar al reservar y al crear.
   *
   * Antes un fallo devolvía "no disponible", y las dos pantallas que la usan
   * traducen cualquier "no disponible" a "tomada": el wizard corta el envío con
   * "ese usuario ya está tomado, elegí otro" y en el módulo el botón de crear
   * queda deshabilitado. O sea que un 429 del límite por IP —que se comparte
   * entre los cuatro endpoints públicos de llave, y que en una oficina o detrás
   * de un CGNAT se agota entre varias personas— le negaba a la gente una llave
   * que estaba perfectamente libre, y sin forma de darse cuenta.
   *
   * Lo que se cede es cosmético: ante un corte de red el campo dice
   * "disponible" sin saberlo, y si de verdad estaba tomada el cliente se entera
   * un paso después, con el mensaje correcto del servidor. Es mejor trato que
   * mandarlo a elegir otro nombre por un problema que no era suyo.
   */
  async consultarDisponibilidad(
    llave: string,
    email?: string
  ): Promise<DisponibilidadLlave> {
    const url = `${this.baseUrl}public/llaves/disponibilidad`;

    // El correo se omite si esta vacio en vez de mandarse en blanco. En el
    // paso 2 del registro el usuario todavia no lo cargo, y un `email: ''`
    // no es un correo opcional ausente: es un correo invalido.
    const cuerpo: { llave: string; email?: string } = email?.trim()
      ? { llave, email: email.trim() }
      : { llave };

    try {
      return await firstValueFrom(
        this.http.post<DisponibilidadLlave>(
          url,
          cuerpo,
          { headers: this.publicHeaders() }
        )
      );
    } catch {
      // No se pudo verificar. Se deja pasar en vez de bloquear: quien decide
      // de verdad es el backend, al reservar y al crear.
      return { disponible: true, llave };
    }
  }

  /**
   * Confirma que la llave del link existe.
   *
   * No devuelve el nombre del dueño: el endpoint es público y devolverlo
   * permitía enumerar clientes. El banner muestra la llave, que es lo que
   * quien abrió el link ya tenía.
   *
   * No lanza: un link mal escrito o un problema de red devuelven
   * `existe: false` y el registro sigue como si nadie lo hubiera invitado.
   */
  async buscarReferidor(llave: string): Promise<ReferidorPublico> {
    const url = `${this.baseUrl}public/llaves/referidor`;
    try {
      return await firstValueFrom(
        this.http.post<ReferidorPublico>(
          url,
          { llave },
          { headers: this.publicHeaders() }
        )
      );
    } catch {
      return { existe: false };
    }
  }

  /**
   * Montos y estado del programa. Publico: se consulta desde el registro.
   *
   * Devuelve `null` si falla. Quien lo use tiene que poder seguir sin el dato
   * — inventar un monto es exactamente el problema que este endpoint resuelve.
   *
   * Se cachea en memoria porque ahora lo consultan cuatro pantallas al cargar
   * (registro, menu, modal y modulo) y el valor cambia cuando alguien toca un
   * parametro, no entre dos clics. El cache muere al recargar la pagina, que
   * es suficiente: encender el programa se ve en la siguiente visita.
   */
  private programaCache: Promise<ProgramaReferidos | null> | null = null;

  async programa(): Promise<ProgramaReferidos | null> {
    if (!this.programaCache) {
      this.programaCache = firstValueFrom(
        this.http.get<ProgramaReferidos>(`${this.baseUrl}public/llaves/programa`, {
          headers: this.publicHeaders(),
        })
      ).catch(() => null);
    }
    return this.programaCache;
  }

  /**
   * `true` si el programa esta encendido.
   *
   * Ante un error devuelve `false`: si no se puede saber, se esconde. Mostrar
   * un campo obligatorio o un menu de una funcion apagada es peor que no
   * mostrarlo.
   */
  async programaActivo(): Promise<boolean> {
    const p = await this.programa();
    return Boolean(p?.activo);
  }

  /** Aparta la llave 48 horas contra el correo, al avanzar del paso 2. */
  async reservar(llave: string, email: string): Promise<ReservaLlave> {
    const url = `${this.baseUrl}public/llaves/reservar`;
    return firstValueFrom(
      this.http.post<ReservaLlave>(
        url,
        { llave, email },
        { headers: this.publicHeaders() }
      )
    );
  }

  /**
   * Avisa que este correo llego por el link de alguien. Se llama al verificarse
   * el correo, en el paso 3.
   *
   * **Va con sesion y sin mandar el correo.** El backend lo saca del token de
   * Cognito. La version anterior pegaba contra `public/llaves/intencion` con el
   * correo en el cuerpo, y al ser publico cualquiera podia declarar que el
   * correo de otro habia llegado por su link y quedarse con un invitado que no
   * trajo. Con campanas asignadas y cupos, eso vacia el acuerdo de un cliente.
   *
   * La sesion ya existe cuando esto corre: `confirmEmailOtp` hace `signIn`
   * antes de llamar. Igual el interceptor puede quedarse sin token si la
   * sesion tarda, y por eso el catch sigue: no lanza nunca. El backend ya
   * responde 200 con un motivo cuando la llave no sirve, y aca se atrapa hasta
   * el error de red. El registro no puede frenarse porque fallo el aviso de un
   * referido.
   */
  async registrarIntencion(llave: string): Promise<ResultadoIntencion> {
    const url = `${this.baseUrl}referidos/intencion`;
    try {
      return await firstValueFrom(
        this.http.post<ResultadoIntencion>(url, { llave })
      );
    } catch {
      return { registrado: false, motivo: 'ERROR_RED' };
    }
  }

  // ---------------------------------------------------------------------
  // Módulo de referidos (con sesión)
  // ---------------------------------------------------------------------

  /** `{ llave: null }` es la señal con la que el front decide mostrar el modal. */
  async miLlave(): Promise<{ llave: string | null }> {
    return firstValueFrom(
      this.http.get<{ llave: string | null }>(`${this.baseUrl}llaves/mi-llave`)
    );
  }

  /** Una sola vez por usuario: el backend devuelve 409 si ya tiene. */
  async crearMiLlave(llave: string): Promise<{ llave: string }> {
    return firstValueFrom(
      this.http.post<{ llave: string }>(`${this.baseUrl}llaves/mi-llave`, { llave })
    );
  }

  async misReferidos(
    page = 1,
    limit = 20
  ): Promise<{ items: ReferidoItem[]; total: number; page: number; limit: number }> {
    return firstValueFrom(
      this.http.get<{
        items: ReferidoItem[];
        total: number;
        page: number;
        limit: number;
      }>(`${this.baseUrl}referidos/mis-referidos?page=${page}&limit=${limit}`)
    );
  }

  async resumen(): Promise<ResumenReferidos> {
    return firstValueFrom(
      this.http.get<ResumenReferidos>(`${this.baseUrl}referidos/resumen`)
    );
  }

  /**
   * ¿Este cliente ve referidos? Ver `MiProgramaReferidos`.
   *
   * No se cachea entre llamadas: la respuesta es de un usuario, y un caché de
   * página se la mostraría al siguiente que inicie sesión en la misma
   * pestaña. Sólo se comparte la llamada en vuelo, porque el menú, el home y
   * el aviso de bienvenida la piden a la vez al entrar.
   *
   * Nunca lanza: ante cualquier error, `visible: false`. No mostrar es el lado
   * seguro, y es también lo que pasa si el API todavía no tiene el endpoint.
   */
  private miProgramaEnVuelo: Promise<MiProgramaReferidos> | null = null;

  miPrograma(): Promise<MiProgramaReferidos> {
    if (!this.miProgramaEnVuelo) {
      this.miProgramaEnVuelo = firstValueFrom(
        this.http.get<MiProgramaReferidos>(`${this.baseUrl}referidos/mi-programa`)
      )
        .then((r) => ({
          visible: Boolean(r?.visible),
          condiciones: r?.visible ? r?.condiciones ?? null : null,
          cuota: r?.cuota ?? SIN_PROGRAMA.cuota,
        }))
        .catch(() => SIN_PROGRAMA)
        .finally(() => {
          this.miProgramaEnVuelo = null;
        });
    }
    return this.miProgramaEnVuelo;
  }

  /**
   * Las condiciones que se le pueden mostrar a un invitado según el documento
   * que eligió, o `null`.
   *
   * La campaña de quien invita tiene que alcanzar ese documento: Chile a
   * `chile`, Venezuela a `venezuela`, y la para todos a los dos. Cualquier
   * otro documento, o todavía ninguno elegido, no ve bono: el registro no le
   * promete nada hasta saber de qué país es.
   */
  condicionesParaDocumento(
    c: CondicionesCampana | null | undefined,
    documento: DocumentoRegistro
  ): CondicionesCampana | null {
    if (!c || (documento !== 'chile' && documento !== 'venezuela')) return null;
    if (c.alcance?.universal) return c;
    const pais = documento === 'chile' ? 'CL' : 'VE';
    return String(c.alcance?.codigo ?? '').toUpperCase() === pais ? c : null;
  }

  // ---------------------------------------------------------------------
  // Compartir (lo usan el modulo y la vitrina del home)
  // ---------------------------------------------------------------------

  /**
   * El link de invitacion de una llave.
   *
   * Vive aca y no en cada pantalla porque ya son dos las que lo arman —el
   * modulo y la tarjeta del home— y una URL escrita dos veces es una URL que
   * en algun momento va a estar escrita de dos formas distintas.
   */
  linkDe(llave: string | null | undefined): string {
    return llave ? `${window.location.origin}/auth/new-signup?llave=${llave}` : '';
  }

  /**
   * El monto como texto, o cadena vacía si no hay número que prometer.
   *
   * Un solo lugar decide cómo se nombra: antes estaba escrito a mano en cinco
   * lados y un cambio de parámetro dejaba al front prometiendo un monto que el
   * backend ya no pagaba. La moneda sale de la campaña; «USDT» es sólo el
   * valor por defecto para quien todavía no la pasa.
   */
  montoTexto(monto: number | null | undefined, moneda = 'USDT'): string {
    return monto ? `${miles(monto)} ${simbolo(moneda)}` : '';
  }

  // ---------------------------------------------------------------------
  // Frases de la campaña
  //
  // Todas las pantallas arman sus textos desde acá, con las condiciones que
  // manda el API. Ninguna escribe montos, pisos ni reglas. Sin condiciones
  // devuelven cadena vacía y la pantalla no promete nada.
  //
  // Frases aprobadas el 27-sep-2026 (docs/referidosMensajes/CHECKLIST.md).
  // ---------------------------------------------------------------------

  /** «15.000 CLP, 15.000 Bs. o 20 USDT». */
  pisosTexto(c: CondicionesCampana | null | undefined): string {
    if (!c) return '';
    const partes = [
      c.pisoFondeoClp ? `${miles(c.pisoFondeoClp)} CLP` : '',
      c.pisoFondeoVes ? `${miles(c.pisoFondeoVes)} Bs.` : '',
      c.pisoFondeoUsdt ? `${miles(c.pisoFondeoUsdt)} USDT` : '',
    ].filter(Boolean);
    if (partes.length <= 1) return partes.join('');
    return `${partes.slice(0, -1).join(', ')} o ${partes[partes.length - 1]}`;
  }

  /** Lo que gana quien invita: «5 USDT». */
  bonoReferidorTexto(c: CondicionesCampana | null | undefined): string {
    return c ? this.montoTexto(c.bonoReferidor, c.moneda) : '';
  }

  /** Lo que gana el invitado, que puede ser otro monto. */
  bonoReferidoTexto(c: CondicionesCampana | null | undefined): string {
    return c ? this.montoTexto(c.bonoReferido, c.moneda) : '';
  }

  /**
   * Lo que tiene que hacer el invitado, como complemento de «hacer»: «una carga
   * de al menos 15.000 CLP, 15.000 Bs. o 20 USDT en una sola operación».
   *
   * «En una sola operación» no es relleno: cuenta UNA carga, no la suma. Sin
   * eso, quien carga 10.000 y 5.000 con un piso de 15.000 reclama un bono que
   * la regla no paga.
   */
  cargaTexto(c: CondicionesCampana | null | undefined): string {
    const pisos = this.pisosTexto(c);
    return pisos ? `una carga de al menos ${pisos} en una sola operación` : '';
  }

  /** Cuándo cobra quien invita, como complemento de «cada persona que…». */
  gatilloTexto(c: CondicionesCampana | null | undefined): string {
    if (!c) return '';
    if (c.gatilloReferidor === 'AL_KYC') return 'valide su identidad';
    if (c.gatilloReferidor === 'AL_PRIMER_ENVIO') return 'haga su primer envío';
    const carga = this.cargaTexto(c);
    return carga ? `haga ${carga}` : '';
  }

  /** Aclaración de alcance, o vacío si la campaña es para todos. */
  alcanceTexto(c: CondicionesCampana | null | undefined): string {
    if (!c || c.alcance?.universal || !c.alcance?.nombre) return '';
    return ` Aplica si te registras con documento de ${c.alcance.nombre}.`;
  }

  /**
   * La misma aclaración dicha a quien invita: sus invitados sólo cobran si
   * se registran con documento de ese país. Vacío si la campaña es para todos.
   */
  alcanceReferidorTexto(c: CondicionesCampana | null | undefined): string {
    if (!c || c.alcance?.universal || !c.alcance?.nombre) return '';
    return `Aplica para quienes se registren con documento de ${c.alcance.nombre}.`;
  }

  /**
   * La barra del registro, después de «Te invitó @X.»: «Si haces una carga de
   * al menos … en una sola operación dentro de los 30 días siguientes a tu
   * registro, ganas 5 USDT.»
   */
  fraseRegistro(c: CondicionesCampana | null | undefined): string {
    const bono = this.bonoReferidoTexto(c);
    const carga = this.cargaTexto(c);
    if (!c || !bono || !carga) return '';
    const plazo =
      c.reservaDias > 0 ? ` dentro de los ${c.reservaDias} días siguientes a tu registro` : '';
    return `Si haces ${carga}${plazo}, ganas ${bono}.${this.alcanceTexto(c)}`;
  }

  /**
   * El encabezado de quien invita: «Ganas 5 USDT por cada persona que abra su
   * cuenta con tu usuario y haga una carga de al menos … en una sola
   * operación. Esa persona también gana 5 USDT.»
   */
  fraseReferidor(c: CondicionesCampana | null | undefined): string {
    const bonoR = this.bonoReferidorTexto(c);
    const gatillo = this.gatilloTexto(c);
    if (!c || !bonoR || !gatillo) return '';
    const base = `Ganas ${bonoR} por cada persona que abra su cuenta con tu usuario y ${gatillo}.`;
    const alcance = this.alcanceReferidorTexto(c);
    const conAlcance = (t: string) => (alcance ? `${t} ${alcance}` : t);
    const bonoI = this.bonoReferidoTexto(c);
    if (!bonoI) return conAlcance(base);
    // Con el gatillo de carga la condición ya se dijo: no se repite.
    if (!c.gatilloReferidor || c.gatilloReferidor === 'AL_FONDEAR') {
      return conAlcance(`${base} Esa persona también gana ${bonoI}.`);
    }
    const carga = this.cargaTexto(c);
    return conAlcance(
      carga ? `${base} Esa persona gana ${bonoI} cuando hace ${carga}.` : base
    );
  }

  /** La condición de actividad de quien invita, o vacío si no se exige. */
  fraseActividad(c: CondicionesCampana | null | undefined): string {
    const dias = Number(c?.actividadReferidorDias) || 0;
    return dias > 0
      ? `Para cobrar, tienes que haber cargado tu cuenta en los últimos ${dias} días.`
      : '';
  }

  /** El plazo que tiene cada invitado, dicho a quien invita. */
  frasePlazo(c: CondicionesCampana | null | undefined): string {
    return c && c.reservaDias > 0
      ? `Cada invitado tiene ${c.reservaDias} días desde su registro para cumplir.`
      : '';
  }

  /** El mensaje con el que se comparte una invitación. */
  textoInvitacion(link: string, c: CondicionesCampana | null | undefined): string {
    const bono = this.bonoReferidoTexto(c);
    const carga = this.cargaTexto(c);
    const premio = bono && carga ? `. Si haces ${carga}, ganas ${bono}` : '';
    // El mensaje lo lee el invitado, que puede ser de otro país: si la campaña
    // es de un país, se le dice antes de que se registre.
    const alcance = premio ? this.alcanceTexto(c).replace(/\.$/, '') : '';
    return `Abre tu cuenta en Aguacate con mi usuario${premio}${alcance ? '.' + alcance : ''}: ${link}`;
  }

  /**
   * «5 USDT · 300 Bs.»: lo ganado (o lo que espera) en cada moneda, sin
   * sumar monedas distintas. Sin `totales` (API anterior) cae al total viejo.
   */
  totalesTexto(r: ResumenReferidos | null | undefined, campo: 'liberado' | 'pendiente'): string {
    if (!r) return '';
    const totales = r.totales;
    if (!totales) {
      const viejo = campo === 'liberado' ? r.liberadoUsdt : r.pendienteUsdt;
      return `${miles(Number(viejo) || 0)} USDT`;
    }
    const partes = totales
      .filter((t) => Number(t[campo]) > 0)
      .map((t) => this.montoTexto(Number(t[campo]), t.moneda));
    return partes.length ? partes.join(' · ') : '0';
  }

  /** Copia al portapapeles. `false` si el navegador no lo permitio. */
  async copiar(texto: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(texto);
      return true;
    } catch {
      return false;
    }
  }

  // ---------------------------------------------------------------------
  // Compliance (operador)
  // ---------------------------------------------------------------------

  /** La configuración vigente, para el formulario del panel. */
  async adminConfig(): Promise<ConfigReferidos> {
    return firstValueFrom(
      this.http.get<ConfigReferidos>(`${this.baseUrl}referidos-control/config`)
    );
  }

  /**
   * Guarda la configuración. Devuelve qué cambió y de qué a qué.
   *
   * Se invalida el caché del estado público: si el operador acaba de encender
   * el programa, la pantalla del cliente tiene que verlo sin recargar todo.
   */
  async adminGuardarConfig(
    cambios: Partial<ConfigReferidos>
  ): Promise<{ cambios: Array<{ clave: string; antes: string; ahora: string }> }> {
    const r = await firstValueFrom(
      this.http.put<{ cambios: Array<{ clave: string; antes: string; ahora: string }> }>(
        `${this.baseUrl}referidos-control/config`,
        cambios
      )
    );
    this.programaCache = null;
    return r;
  }

  /** El tablero del programa: gasto, deuda retenida y conversión. */
  async adminResumen(): Promise<ResumenAdmin> {
    return firstValueFrom(
      this.http.get<ResumenAdmin>(`${this.baseUrl}referidos-control/resumen`)
    );
  }

  /**
   * Los vínculos, paginado y filtrable.
   *
   * Los filtros vacíos no viajan: mandar `estado=` sin valor haría que el
   * backend filtre por cadena vacía y no devuelva nada.
   */
  async adminBonos(
    filtros: FiltrosBonos = {}
  ): Promise<{ items: BonoAdminItem[]; total: number; page: number; limit: number }> {
    const params = new URLSearchParams();
    params.set('page', String(filtros.page ?? 1));
    params.set('limit', String(filtros.limit ?? 20));
    if (filtros.estado) params.set('estado', filtros.estado);
    if (filtros.bono) params.set('bono', filtros.bono);
    if (filtros.q?.trim()) params.set('q', filtros.q.trim());

    return firstValueFrom(
      this.http.get<{
        items: BonoAdminItem[];
        total: number;
        page: number;
        limit: number;
      }>(`${this.baseUrl}referidos-control/bonos?${params.toString()}`)
    );
  }

  /**
   * Quienes traen mas gente, ordenado por invitados que efectivamente cargaron.
   *
   * Paginado desde que hay campanas asignadas: el listado dejo de ser «los
   * veinte de siempre» y hay que poder recorrerlo entero para decidir a quien
   * asignarle un acuerdo.
   */
  async adminTopReferidores(page = 1, limit = 20): Promise<Pagina<TopReferidor>> {
    return firstValueFrom(
      this.http.get<Pagina<TopReferidor>>(
        `${this.baseUrl}referidos-control/top-referidores?page=${page}&limit=${limit}`
      )
    );
  }

  // -------------------------------------------------------------------------
  // Campanas
  // -------------------------------------------------------------------------

  private get campanasUrl(): string {
    return `${this.baseUrl}referidos-control/campanas`;
  }

  /** Lo que el formulario puede ofrecer sin proponer nada imposible. */
  async campanaOpciones(): Promise<OpcionesCampana> {
    return firstValueFrom(this.http.get<OpcionesCampana>(`${this.campanasUrl}/opciones`));
  }

  async campanas(filtros: {
    page?: number;
    limit?: number;
    estado?: string;
    tipo?: string;
    q?: string;
  } = {}): Promise<Pagina<CampanaReferidos>> {
    const params = new URLSearchParams();
    params.set('page', String(filtros.page ?? 1));
    params.set('limit', String(filtros.limit ?? 20));
    // Los filtros vacios no viajan: mandar `estado=` sin valor haria que el
    // backend filtre por cadena vacia y no devuelva nada.
    if (filtros.estado) params.set('estado', filtros.estado);
    if (filtros.tipo) params.set('tipo', filtros.tipo);
    if (filtros.q?.trim()) params.set('q', filtros.q.trim());

    return firstValueFrom(
      this.http.get<Pagina<CampanaReferidos>>(`${this.campanasUrl}?${params.toString()}`)
    );
  }

  async crearCampana(dto: CrearCampanaReferidos): Promise<CampanaReferidos> {
    return firstValueFrom(this.http.post<CampanaReferidos>(this.campanasUrl, dto));
  }

  /**
   * Edita una campaña. Que se pueda o no depende del estado, y lo decide el
   * backend: con la campaña viva no deja tocar la moneda ni los montos.
   */
  async editarCampana(
    id: number,
    dto: Partial<CrearCampanaReferidos>
  ): Promise<CampanaReferidos> {
    return firstValueFrom(
      this.http.put<CampanaReferidos>(`${this.campanasUrl}/${id}`, dto)
    );
  }

  /** `activar`, `pausar`, `reanudar` o `cerrar`. */
  async accionCampana(
    id: number,
    accion: 'activar' | 'pausar' | 'reanudar' | 'cerrar'
  ): Promise<CampanaReferidos> {
    return firstValueFrom(
      this.http.put<CampanaReferidos>(`${this.campanasUrl}/${id}/${accion}`, {})
    );
  }

  /**
   * El libro XLS de la campaña, armado en el servidor: quién cobró bono y qué
   * hizo con la plata (cargas, conversiones, envíos, retiros y saldo hoy).
   *
   * Lo arma el backend y no el panel porque cruza los tres libros de saldo y
   * las transacciones de cada persona: traer todo eso al navegador para sumar
   * acá sería mover miles de asientos que el operador no va a mirar uno a uno.
   */
  async libroCampana(id: number): Promise<{ archivo: Blob; nombre: string }> {
    const r = await firstValueFrom(
      this.http.get(`${this.campanasUrl}/${id}/reportes/libro`, {
        responseType: 'blob',
        observe: 'response',
      })
    );
    const disposicion = r.headers.get('Content-Disposition') ?? '';
    const nombre =
      /filename="?([^";]+)"?/.exec(disposicion)?.[1] ??
      `referidos-campana-${id}-${new Date().toISOString().slice(0, 10)}.xlsx`;
    return { archivo: r.body as Blob, nombre };
  }

  async asignaciones(
    id: number,
    page = 1,
    limit = 20
  ): Promise<Pagina<AsignacionCampana>> {
    return firstValueFrom(
      this.http.get<Pagina<AsignacionCampana>>(
        `${this.campanasUrl}/${id}/referidores?page=${page}&limit=${limit}`
      )
    );
  }

  /**
   * Asigna el link mejorado. Devuelve tambien los omitidos con el motivo:
   * asignarle recompensa a quien compliance no aprobo es prometerle algo que
   * nunca va a poder cobrar, y conviene saberlo aca.
   */
  async asignarReferidores(
    id: number,
    dto: {
      referidores: string[];
      cuota?: number;
      exclusiva?: boolean;
      desde?: string;
      hasta?: string;
    }
  ): Promise<{ asignados: number; omitidos: Array<{ referencia: string; motivo: string }> }> {
    return firstValueFrom(
      this.http.post<{
        asignados: number;
        omitidos: Array<{ referencia: string; motivo: string }>;
      }>(`${this.campanasUrl}/${id}/referidores`, dto)
    );
  }

  async desasignarReferidor(id: number, usuarioId: number): Promise<{ desasignado: boolean }> {
    return firstValueFrom(
      this.http.delete<{ desasignado: boolean }>(
        `${this.campanasUrl}/${id}/referidores/${usuarioId}`
      )
    );
  }

  /** Riesgo, fuga y retorno de una campana. */
  async tableroCampana(id: number): Promise<TableroCampana> {
    return firstValueFrom(this.http.get<TableroCampana>(`${this.campanasUrl}/${id}/tablero`));
  }

  /** Lo mismo sin filtrar: como se mira la primera vez que se abre el panel. */
  async tableroPrograma(): Promise<{ fuga: FugaCampana; retorno: RetornoCampana }> {
    return firstValueFrom(
      this.http.get<{ fuga: FugaCampana; retorno: RetornoCampana }>(
        `${this.campanasUrl}/tablero/programa`
      )
    );
  }

  async rendimientoCampanas(page = 1, limit = 20): Promise<Pagina<RendimientoCampana>> {
    return firstValueFrom(
      this.http.get<Pagina<RendimientoCampana>>(
        `${this.campanasUrl}/rendimiento/campanas?page=${page}&limit=${limit}`
      )
    );
  }

  async rendimientoReferidores(filtros: {
    page?: number;
    limit?: number;
    campanaId?: number;
  } = {}): Promise<Pagina<RendimientoReferidor>> {
    const params = new URLSearchParams();
    params.set('page', String(filtros.page ?? 1));
    params.set('limit', String(filtros.limit ?? 20));
    if (filtros.campanaId) params.set('campanaId', String(filtros.campanaId));

    return firstValueFrom(
      this.http.get<Pagina<RendimientoReferidor>>(
        `${this.campanasUrl}/rendimiento/referidores?${params.toString()}`
      )
    );
  }

  /**
   * Cuanto se puso y cuanto volvio, por campana y en total.
   *
   * `totales` viene aparte del listado a proposito: sumar las paginas de una
   * tabla paginada da el total de la pagina, no el del programa.
   */
  async inversionRetorno(page = 1, limit = 20): Promise<InversionRetorno> {
    return firstValueFrom(
      this.http.get<InversionRetorno>(
        `${this.campanasUrl}/reportes/inversion-retorno?page=${page}&limit=${limit}`
      )
    );
  }

  /**
   * Todos los que ganaron algo, por persona.
   *
   * Es la diferencia con `usuariosConLlave`, que lista vinculos y mira al
   * invitado: aca la fila es alguien, y suma sus dos papeles.
   */
  async usuariosConBono(filtros: {
    page?: number;
    limit?: number;
    q?: string;
    estado?: string;
    campanaId?: number;
  } = {}): Promise<Pagina<UsuarioConBono>> {
    const params = new URLSearchParams();
    params.set('page', String(filtros.page ?? 1));
    params.set('limit', String(filtros.limit ?? 20));
    if (filtros.q?.trim()) params.set('q', filtros.q.trim());
    if (filtros.estado) params.set('estado', filtros.estado);
    if (filtros.campanaId) params.set('campanaId', String(filtros.campanaId));

    return firstValueFrom(
      this.http.get<Pagina<UsuarioConBono>>(
        `${this.campanasUrl}/usuarios/ganancias?${params.toString()}`
      )
    );
  }

  /** Todos los registrados con llave, incluidos los que no terminaron el alta. */
  async usuariosConLlave(filtros: {
    page?: number;
    limit?: number;
    q?: string;
    reclamo?: string;
    campanaId?: number;
  } = {}): Promise<Pagina<UsuarioConLlave>> {
    const params = new URLSearchParams();
    params.set('page', String(filtros.page ?? 1));
    params.set('limit', String(filtros.limit ?? 20));
    if (filtros.q?.trim()) params.set('q', filtros.q.trim());
    if (filtros.reclamo) params.set('reclamo', filtros.reclamo);
    if (filtros.campanaId) params.set('campanaId', String(filtros.campanaId));

    return firstValueFrom(
      this.http.get<Pagina<UsuarioConLlave>>(
        `${this.campanasUrl}/usuarios/con-llave?${params.toString()}`
      )
    );
  }

  /**
   * Ficha de referidos de un usuario, para la pantalla de compliance.
   *
   * Va por separado porque la ficha se alimenta de la lambda de wallet-back,
   * cuya entidad `User` no conoce la columna: el campo no llega por ahi y ese
   * repo queda fuera del alcance.
   *
   * No lanza. Si falla, la ficha se dibuja sin el bloque en vez de romperse
   * entera por un dato secundario.
   */
  async fichaCompliance(usuarioId: number): Promise<FichaReferidos | null> {
    try {
      return await firstValueFrom(
        this.http.get<FichaReferidos>(`${this.baseUrl}referidos-control/usuario/${usuarioId}`)
      );
    } catch {
      return null;
    }
  }

  // ---------------------------------------------------------------------
  // Reglas de la llave, iguales a las del backend
  // ---------------------------------------------------------------------

  /**
   * Normaliza como lo hace el backend: sin `@`, sin espacios, en mayúscula.
   *
   * No translitera tildes ni ñ a propósito: `NIÑA1234` se rechaza en vez de
   * volverse `NINA1234`, que sería una llave distinta de la que el usuario
   * escribió y encima colisionable con la de otro.
   */
  normalizar(entrada: string): string {
    return (entrada ?? '').trim().replace(/^@+/, '').toUpperCase();
  }

  /** Valida formato en el cliente para no ir al servidor por cada tecla. */
  validarFormato(entrada: string): { valida: boolean; mensaje?: string } {
    const llave = this.normalizar(entrada);

    if (!llave) return { valida: false, mensaje: 'Escribe tu usuario.' };

    if (llave.length < 8) {
      return {
        valida: false,
        mensaje: `El usuario necesita al menos 8 caracteres. Te faltan ${8 - llave.length}.`,
      };
    }

    if (llave.length > 10) {
      return {
        valida: false,
        mensaje: `El usuario puede tener hasta 10 caracteres. Te sobran ${llave.length - 10}.`,
      };
    }

    if (!/^[A-Z0-9]{8,10}$/.test(llave)) {
      return {
        valida: false,
        mensaje:
          'El usuario sólo admite letras sin tilde y números. Quita los acentos, la ñ, los espacios y los símbolos.',
      };
    }

    return { valida: true };
  }
}

/** 15000 → «15.000». Sin depender del locale del navegador. */
function miles(n: number): string {
  const [entero, decimales] = String(Math.round(n * 100) / 100).split('.');
  const conPuntos = entero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return decimales ? `${conPuntos},${decimales}` : conPuntos;
}

/** Cómo se nombra cada moneda en los textos al cliente. */
function simbolo(moneda: string): string {
  return moneda === 'VES' ? 'Bs.' : moneda;
}
